import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Grid, IconButton, TextField, Typography, useTheme, lighten } from "@mui/material";
import React, { forwardRef } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "src/custom-hooks/redux/redux";
import { isAuthorizedEeGroup } from "src/custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "src/utils/constants/authorization-constants";
import { Props } from "./interfaces";
import {
  EmployeeInfoContainer,
  InfoContainer,
  IconsContainer,
  NameContainer,
  DateDocumentContainer,
  TitleName,
  BusinessDescriptionContainer,
  DescriptionsContainer,
} from "./styles";
import {
  EmployeeIcon,
  MageCalendarIcon,
  BagIcon,
  TextDescriptionIcon,
  MoneyIcon,
} from "../../../../../../assets/icons";
import AddNewDocument from "../../../../../../containers/AddNewDocument/AddNewDocument";
import AttachedDocuments from "../../../../../../containers/AttachedDocuments/AttachedDocuments";
import AttachedFiles from "../../../../../../containers/AttachedFiles/AttachedFiles";
import { DatePickerWrapper } from "../../../../../../containers/FilterByYear/styles";
import { DocumentCategoryRef } from "../../../../../../models/document-categories";
import { DocumentTypeRef } from "../../../../../../models/document-types";
import { DocumentDTO } from "../../../../../../models/documents";
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPES,
  EXPENSE_REPORT_STATUS,
} from "../../../../../../utils/constants/constants";
import { ROW_CLASSNAME } from "../../../../../../utils/export-expense-reports-utils/export-expense-reports-as-pdf";
import { getNormaleDateOrMonth } from "../../../../../../utils/time-utils";
import { intlFormatDateShort } from "../../../../../../utils/time-utils";
import { ExpenseReportView } from "../../../utils";

const EmployeeInfoSection = ({
  employee,
  handleChange,
  date,
  dateRange,
  description,
  businessPurposeDescription,
  preApprovalDocument,
  legalEntity,
  currency,
  totalAmount,
  view,
  handleNotifyOnDocumentChange,
  changeDocCCCDStatus,
  readOnly,
  forExport,
  auditorComment,
  differenceAmount,
  allTravelExpenseAudited,
  status,
  handleAddAuditorComment,
}: Props) => {
  const { t, i18n } = useTranslation();
  const tRP = "components.add_edit_expense_report";

  const theme = useTheme();

  const getCurrentLanguage = (): string => (i18n.language === "en" ? "enGB" : i18n.language);

  const CustomInput = forwardRef<
    HTMLButtonElement,
    {
      value: string;
      onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    }
  >(({ value, onClick }, ref) => (
    <Button
      variant="outlined"
      onClick={onClick}
      ref={ref}
      disabled={readOnly}
      style={{
        fontSize: "0.8rem",
        padding: "3px 3px",
      }}
    >
      {value}
    </Button>
  ));

  CustomInput.displayName = "CustomInput";

  const currentUserAuthorizations = useAppSelector((state) => state.currentUserReducer.currentUserAuthorizations);
  const isAuditor = employee.groups
    ? isAuthorizedEeGroup(
        [AUTHORIZATIONS.EmployeesAccess.AuditTravelAndExpenses],
        currentUserAuthorizations,
        employee?.groups?.map((group) => group?.id) ?? [],
        true
      )
    : false;

  const allowToEditAuditorComment =
    isAuditor &&
    !forExport &&
    differenceAmount !== totalAmount &&
    allTravelExpenseAudited &&
    status?.id !== EXPENSE_REPORT_STATUS.paid.id;

  const allowToViewAuditorComment = (!isAuditor || status?.id === EXPENSE_REPORT_STATUS.paid.id) && !!auditorComment;

  return (
    <EmployeeInfoContainer
      container
      className={ROW_CLASSNAME}
      forExport={forExport}
    >
      {!forExport && (
        <InfoContainer container>
          <IconsContainer>
            <EmployeeIcon style={{ width: "30px", height: "30px" }} />
          </IconsContainer>

          <NameContainer flex={1}>
            <TitleName>{`${t(`${tRP}.form.fields.employee`)}:`}</TitleName>
            <Typography>{employee?.name ?? ""}</Typography>
          </NameContainer>
        </InfoContainer>
      )}

      <InfoContainer
        container
        sx={{
          alignItems: "center",
        }}
      >
        <IconsContainer>
          <MageCalendarIcon style={{ width: "30px", height: "30px" }} />
        </IconsContainer>

        <DateDocumentContainer
          flex={1}
          readOnly={readOnly && !forExport}
        >
          <TitleName>{`${t(`${tRP}.form.fields.date`)}:`}</TitleName>

          {!readOnly && (
            <DatePickerWrapper>
              <DatePicker
                onChange={(value) => {
                  !!value && !!handleChange && handleChange("date", value);
                }}
                customInput={React.createElement(CustomInput)}
                dateFormat="yyyy MMMM dd"
                locale={getCurrentLanguage()}
                popperPlacement="auto-end"
                popperProps={{
                  zIndex: 5,
                }}
                selected={date ? new Date(date) : new Date()}
                disabled={readOnly}
                minDate={dateRange?.minDate ? new Date(dateRange?.minDate) : null}
                maxDate={dateRange?.maxDate ? new Date(dateRange?.maxDate) : null}
              />
            </DatePickerWrapper>
          )}

          {readOnly && <Typography>{intlFormatDateShort(date)}</Typography>}
        </DateDocumentContainer>
      </InfoContainer>

      <InfoContainer container>
        <IconsContainer>
          <BagIcon style={{ width: "30px", height: "30px" }} />
        </IconsContainer>

        <DescriptionsContainer
          flex={1}
          readOnly={readOnly}
        >
          {!readOnly && (
            <TextField
              label={`${t(`${tRP}.form.fields.business_purpose_description`)}`}
              style={{ width: "100%" }}
              value={businessPurposeDescription}
              multiline
              maxRows={3}
              onChange={(event) => {
                handleChange && handleChange("businessPurposeDescription", event.currentTarget.value);
              }}
              disabled={readOnly}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}

          {readOnly && (
            <>
              <TitleName>{`${t(`${tRP}.form.fields.business_purpose_description`)}:`}</TitleName>
              <Typography>{businessPurposeDescription}</Typography>
            </>
          )}
        </DescriptionsContainer>
      </InfoContainer>

      <InfoContainer
        container
        sx={{
          ...(forExport ? { alignItems: "center" } : {}),
        }}
      >
        <IconsContainer>
          <BagIcon style={{ width: "30px", height: "30px" }} />
        </IconsContainer>

        <DateDocumentContainer
          flex={1}
          sx={{ height: "100%" }}
        >
          <BusinessDescriptionContainer isReadOnlyWithoutDoc={readOnly && !preApprovalDocument && !forExport}>
            <TitleName>{`${t(`${tRP}.form.fields.business_purpose_document`)}:`}</TitleName>

            <>
              {view === ExpenseReportView.manager && (
                <>
                  {!!preApprovalDocument && (
                    <AttachedFiles
                      document={preApprovalDocument as DocumentDTO}
                      customFileName={`${new Date(date).getFullYear()} ${getNormaleDateOrMonth(
                        new Date(date).getMonth() + 1
                      )} ${getNormaleDateOrMonth(new Date(date).getDate())} ${legalEntity?.code} ${t(
                        `${tRP}.form.fields.expense_note_justification`
                      )} ${employee?.name} ${totalAmount} ${currency?.code ?? ""}`}
                    />
                  )}

                  {!preApprovalDocument && (
                    <Typography
                      variant="body2"
                      style={{ color: theme.palette.secondary.light }}
                    >
                      <i>{`${t(`${tRP}.form.fields.no_uploaded_document`)}`}</i>
                    </Typography>
                  )}
                </>
              )}

              {view === ExpenseReportView.employee && (
                <>
                  {!readOnly ? (
                    preApprovalDocument ? (
                      <Grid sx={{ display: "flex", flexDirection: "row", flexWrap: "nowrap" }}>
                        <AttachedDocuments
                          count={preApprovalDocument?.filesCount}
                          documentIDs={[preApprovalDocument?.id]}
                          buttonSize="2x"
                          documentsReadOnlyFields={[
                            {
                              documentID: preApprovalDocument?.id,
                              readOnlyFields: {
                                isCategoryRO: true,
                                isLegalEntityRO: true,
                                isTypeRO: true,
                                isDateRO: true,
                                isStatusRO: true,
                                isEmployeeRO: true,
                              },
                            },
                          ]}
                          hideReadOnlyFields={true}
                          useSimpleView={true}
                          handleReturnDocumentToParent={handleNotifyOnDocumentChange}
                          documentsNames={[preApprovalDocument?.name]}
                          customDocAndFilesNames={{
                            customFolderName: `${new Date(date).getFullYear()} ${getNormaleDateOrMonth(
                              new Date(date).getMonth() + 1
                            )} ${getNormaleDateOrMonth(new Date(date).getDate())} ${legalEntity?.code} ${t(
                              `${tRP}.form.fields.expense_note_justification`
                            )} ${employee?.name} ${totalAmount} ${currency?.code ?? ""}`,
                            customFilesName: `${new Date(date).getFullYear()} ${getNormaleDateOrMonth(
                              new Date(date).getMonth() + 1
                            )} ${getNormaleDateOrMonth(new Date(date).getDate())} ${legalEntity?.code} ${t(
                              `${tRP}.form.fields.expense_note_justification`
                            )} ${employee?.name} ${totalAmount} ${currency?.code ?? ""}`,
                          }}
                        />
                        <IconButton
                          style={{ fontSize: "0.8rem" }}
                          onClick={changeDocCCCDStatus}
                        >
                          <FontAwesomeIcon icon={faClose} />
                        </IconButton>
                      </Grid>
                    ) : (
                      <AddNewDocument
                        buttonType="plusButton"
                        useMiniPlusButton
                        plusButtonStyle={{
                          backgroundColor: theme.palette.primary.main,
                          width: "23px",
                          height: "22px",
                          color: theme.palette.primary.contrastText,
                        }}
                        handleNotifyParent={(document) => {
                          document && !!handleNotifyOnDocumentChange && handleNotifyOnDocumentChange(document);
                        }}
                        displayExpandableItemUponSave={false}
                        documentFieldsProps={{
                          employee,
                          legalEntity,
                          documentDate: new Date(),
                          category: DOCUMENT_CATEGORIES.ACCOUNTING_INPUT as DocumentCategoryRef,
                          type: DOCUMENT_TYPES.TravelExpensesReceipt as DocumentTypeRef,
                        }}
                        readOnlyFields={{
                          isEmployeeRO: true,
                          isCategoryRO: true,
                          isLegalEntityRO: true,
                          isTypeRO: true,
                          isDateRO: true,
                          isStatusRO: true,
                        }}
                        hideReadOnlyFields={true}
                        maxFilesNumber={1}
                        infoMessage={`${t(`${tRP}.form.fields.document_upload_info`)}`}
                      />
                    )
                  ) : (
                    <>
                      {!!preApprovalDocument && (
                        <AttachedFiles
                          document={preApprovalDocument as DocumentDTO}
                          customFileName={`${new Date(date).getFullYear()} ${getNormaleDateOrMonth(
                            new Date(date).getMonth() + 1
                          )} ${getNormaleDateOrMonth(new Date(date).getDate())} ${legalEntity?.code} ${t(
                            `${tRP}.form.fields.expense_note_justification`
                          )} ${employee?.name} ${totalAmount} ${currency?.code ?? ""}`}
                        />
                      )}

                      {!preApprovalDocument && (
                        <Typography
                          variant="body2"
                          style={{ color: theme.palette.secondary.light }}
                        >
                          <i>{`${t(`${tRP}.form.fields.no_uploaded_document`)}`}</i>
                        </Typography>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          </BusinessDescriptionContainer>
        </DateDocumentContainer>
      </InfoContainer>

      <InfoContainer container>
        <IconsContainer>
          <TextDescriptionIcon style={{ width: "30px", height: "30px" }} />
        </IconsContainer>

        <DescriptionsContainer flex={1}>
          {!readOnly && (
            <TextField
              label={`${t(`${tRP}.form.fields.description`)}`}
              style={{ width: "100%" }}
              value={description}
              multiline
              maxRows={2}
              onChange={(event) => {
                handleChange && handleChange("description", event.currentTarget.value);
              }}
              disabled={readOnly}
              InputLabelProps={{
                shrink: true,
              }}
            />
          )}

          {readOnly && (
            <>
              <TitleName>{`${t(`${tRP}.form.fields.description`)}:`}</TitleName>
              <Typography>{description}</Typography>
            </>
          )}
        </DescriptionsContainer>
      </InfoContainer>
      {allowToEditAuditorComment && (
        <>
          <InfoContainer>
            <IconsContainer>
              <MoneyIcon
                style={{ width: "30px", height: "30px", color: lighten(theme.palette.secondary.light, 0.25) }}
              />
            </IconsContainer>
            <Grid>
              <TitleName>{`${t(`${tRP}.form.fields.difference_amount_explanation`)}`} :</TitleName>
              <TextField
                // label={`${t(`${tRP}.form.fields.auditor_comment`)}`}
                style={{ width: "100%" }}
                value={auditorComment}
                multiline
                maxRows={2}
                onChange={(event) => {
                  handleChange && handleChange("auditorComment", event.currentTarget.value);
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <Typography
                variant="body2"
                style={{ color: theme.palette.secondary.light }}
              >
                <i>{`${t(`${tRP}.form.fields.please_provide_explanation`)}`}</i>
              </Typography>
            </Grid>
            {
              <Button
                sx={{ boxShadow: 3 }}
                style={{ marginTop: "30px", fontSize: "12px", textTransform: "none" }}
                onClick={handleAddAuditorComment}
                variant="contained"
              >
                {`${t("buttons.save")}`}
              </Button>
            }
          </InfoContainer>
        </>
      )}
      {allowToViewAuditorComment && (
        <InfoContainer>
          <IconsContainer>
            <MoneyIcon style={{ width: "30px", height: "30px", color: lighten(theme.palette.secondary.light, 0.25) }} />
          </IconsContainer>
          <DescriptionsContainer>
            <TitleName>{`${t(`${tRP}.form.fields.difference_amount_explanation`)}`} :</TitleName>
            <Typography>{auditorComment}</Typography>
          </DescriptionsContainer>
        </InfoContainer>
      )}
    </EmployeeInfoContainer>
  );
};

export default React.memo(EmployeeInfoSection);
